import { mapActions, mapGetters } from 'vuex'
import Configuration from '@/agilis/util/Configuration'
import L from 'lodash'
import i18n from '../../../../i18n/i18n'
import toursMixin from '@/agilis/components/Tour/mixins/toursMixin'
import InlineEditor from '@/agilis/components/InlineEditor/InlineEditor.vue'
import jsonLogic from '@/plugins/jsonLogic'

// import { validateWackageDates } from './wpValalidationUtils'

const WP_TYPE_SERVICE = 'service'
const WP_TYPE_RESALE = 'resale'

function getPriceScaleFactor (product, jobType) {
  let multiplier

  switch (jobType) {
    case 'Festivo / Notturno':
      multiplier = product?.pricePercentMultiplerNonWorkingDay || 0
      break

    case 'OOH':
      multiplier = product?.pricePercentMultiplerOutOfOffice || 0
      break

    default:
      multiplier = 0
  }

  const scaleFactor = 1 + (multiplier / 100)

  return scaleFactor
}

const SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC = {
  jsonLogic: {
    '!!': { var: 'externalWorkpackage' }
  }
}

const DISABLED_WHEN_EXTERNAL_SUPPLIER_NOT_SET = {
  jsonLogic: {
    '!': { var: 'externalSupplier.id' }
  }
}

const DISABLED_WHEN_SUPPLIER_NOT_SET = {
  jsonLogic: {
    '!': { var: 'supplier.id' }
  }
}

const WP_TYPE_EQUALS_SERVICE = {
  jsonLogic: {
    '==': [
      { var: 'wpType' },
      WP_TYPE_SERVICE
    ]
  }
}

const EXTERNAL_COST_DISABLED = {
  jsonLogic: {
    'or': [
      WP_TYPE_EQUALS_SERVICE.jsonLogic,
      DISABLED_WHEN_EXTERNAL_SUPPLIER_NOT_SET.jsonLogic
    ]
  }
}

// //////////////////////////////////////////////////////////////////
// JsonLogi for dynamic labels
// //////////////////////////////////////////////////////////////////
const PROFILE_LABEL_LOGIC = {
  jsonLogic: {
    'if': [
      {
        '==': [
          { var: 'wpType' },
          WP_TYPE_RESALE
        ]
      },
      'sales.label.wpProduct',
      'sales.label.wpProfile'
    ]
  }
}

const EXTERNAL_SUPPLIER_LABEL_LOGIC = {
  jsonLogic: {
    'if': [
      {
        '==': [
          { var: 'wpType' },
          WP_TYPE_RESALE
        ]
      },
      'sales.label.wpDistributor',
      'sales.label.wpSupplier'
    ]
  }
}

export default {
  name: 'WorkPackage',
  mixins: [toursMixin],

  props: {
    configuration: Object,
    selectedContext: Object,
    trackerDTO: Object
  },

  components: {
    InlineEditor
  },

  data: () => ({
    dialog: false,
    dialogDelete: false,
    items: [],
    editedIndex: -1,
    editedItem: {
      title: '',
      description: '',
      externalCost: null,
      externalCostNegotiated: null,
      externalWorkpackage: false,
      externalProduct: null,
      externalSupplier: null,
      quantity: 1,
      supplier: null,
      product: null,
      jobType: null,
      cost: null,
      listPrice: null,
      unitPrice: null,
      city: null,
      startDate: null,
      endDate: null,
      sequence: 1,
      wpType: null,
      currency: null
    },
    defaultItem: {
      title: '',
      description: '',
      supplier: null,
      externalCost: null,
      externalListPrice: null,
      externalUnitPrice: null,
      externalWorkpackage: false,
      externalProduct: null,
      externalSupplier: null,
      quantity: 1,
      unitOfMeasure: null,
      product: null,
      jobType: null,
      cost: null,
      listPrice: null,
      unitPrice: null,
      city: null,
      startDate: null,
      endDate: null,
      sequence: 1,
      wpType: null,
      currency: null
    },
    formRows: [
      { cols: ['title'] },
      { cols: ['description'] },
      { cols: ['supplier', 'product'] },
      { cols: ['jobType', 'quantity', 'unitOfMeasure', 'city'] },
      { cols: ['cost', 'listPrice', 'unitPrice', 'currency'] },
      // { cols: ['startDate', 'endDate'] },
      { cols: ['externalWorkpackage'] },
      { cols: ['externalSupplier', 'externalProduct'] },
      { cols: ['externalCost', 'externalListPrice', 'externalUnitPrice'] }
    ]
  }),

  computed: {
    ...mapGetters({
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      workspaceId: 'contextStore/workspaceId',
      loading: 'loaderStore/loading'
    }),
    computeHeaders () {
      const hh = [
        { text: this.$t('sales.label.wpType'), value: 'wpType', sortable: false, width: '60px' },
        { text: this.$t('sales.label.wpTitle'), value: 'title', sortable: false },
        { text: `${this.$t('sales.label.wpProfile')} / ${this.$t('sales.label.wpProduct')}`, value: 'product.name', sortable: false },
        { text: this.$t('sales.label.wpJobType'), value: 'jobType', sortable: false },
        { text: this.$t(this.getQuantityTitleWithType()), value: 'quantity', align: 'end', sortable: false },
        { text: `${this.$t('sales.label.wpSupplier')} / ${this.$t('sales.label.wpDistributor')}`, value: 'externalSupplier', sortable: false },
        { text: this.$t('sales.label.wpCost'), value: 'cost', align: 'end', sortable: false },
        { text: this.$t('sales.label.wpListPrice'), value: 'listPrice', align: 'end', sortable: false },
        { text: this.$t('sales.label.wpUnitPrice'), value: 'unitPrice', align: 'end', sortable: false },
        this.priceGuidance ? { text: this.$t('sales.label.wpPriceGuidance'), value: 'priceGuidance', sortable: false, width: '60px' } : null,
        { text: this.$t('sales.label.wpTotalPrice'), value: 'total', align: 'end', sortable: false },
        { text: this.$t('sales.label.currency'), value: 'currency', align: 'end', sortable: false },
        { text: this.$t('sales.label.variance'), value: 'variance', align: 'end', sortable: false },
        { text: this.$t('sales.label.actions'), value: 'actions', sortable: false, width: '80px' }
      ]

      return hh.filter(h => h && this.userCanAccessField(h.value))
    },
    totalHeadersIndex () {
      let total = this.computeHeaders.length - 9
      if (!this.userCanAccessField('cost')) {
        total += 1
      }
      if (!this.userCanAccessField('listPrice')) {
        total += 1
      }
      if (!this.userCanAccessField('variance')) {
        total += 1
      }
      if (!this.userCanAccessField('total')) {
        total += 1
      }
      return total
    },
    defaultSupplier () {
      const defaultSupplierMap = this.configuration?.workpackages?.defaultSupplier
      const workspaceId = this.workspaceId.split('.')[0]

      let defaultSupplier
      if (defaultSupplierMap && defaultSupplierMap[workspaceId]) {
        defaultSupplier = defaultSupplierMap[workspaceId]
      } else {
        console.warn(`Workspace "${this.workspaceId}" does not have a mappping for default workpackage supplier`)
        defaultSupplier = defaultSupplierMap['__default']
      }
      return defaultSupplier
    },

    changeStateRerenderKey () {
      return this.trackerDTO.id + '_' + this.trackerDTO.state
    },

    wpType () {
      return this.wpTypes.find(t => t.id === this.editedItem.wpType)
    },

    wpTypes () {
      // The id/label below should define the wpTypes: each wpTypes defines which product types are supported
      // NB: as in the wp is not stored the wpType, an existing wp will be mapped using the 1st wpType matching the productType
      return [
        {
          id: WP_TYPE_SERVICE,
          label: this.$t('sales.label.wpTypeService'),
          icon: 'mdi-account-wrench',
          productTypes: ['Servizi']
        }
        // TODO management of resale wp in the next sprint
        /* {
          id: WP_TYPE_RESALE,
          label: this.$t('sales.label.wpTypeResale'),
          icon: 'mdi-cart',
          formTitleEdit: 'sales.label.wpEditResale',
          formTitleNew: 'sales.label.wpNewResale',
          productTypes: ['Resale'],
          defaultValues: {
            externalWorkpackage: true
          }
        } */
      ]
    },

    formFields () { // Why computed? Because there is a piece dynamic on prop "configuration": quantity > label
      return {
        title: {
          id: 'title',
          label: 'sales.label.wpTitle',
          type: 'text',
          required: true,
          veeRules: {
            'unique': {
              message: 'sales.error.propositionTitleUnique'
            }
          }
        },
        description: {
          id: 'description',
          label: 'sales.label.wpDescrition',
          type: 'textarea',
          rows: '3'
        },
        externalCost: {
          id: 'externalCost',
          type: 'number',
          label: 'sales.label.wpCost',
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          propsWhen: {
            disabled: EXTERNAL_COST_DISABLED
          }
        },
        externalListPrice: {
          id: 'externalListPrice',
          type: 'number',
          label: 'sales.label.wpListPrice',
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          propsWhen: {
            disabled: EXTERNAL_COST_DISABLED
          }
        },
        externalUnitPrice: {
          id: 'externalUnitPrice',
          type: 'number',
          label: 'sales.label.wpUnitPrice',
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          propsWhen: {
            disabled: DISABLED_WHEN_EXTERNAL_SUPPLIER_NOT_SET
          },
          rules: { min_value: 0 }
        },
        externalProduct: {
          id: 'externalProduct',
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          type: 'select',
          autocomplete: true,
          itemValue: 'id',
          itemText: 'product',
          itemTextExpr: '$name',
          outValue: 'externalProduct',
          outValueExpr: {
            'id': '$id',
            'cost': '$cost',
            'listPrice': '$listPrice',
            'pricePercentMultiplerOutOfOffice': '$pricePercentMultiplerOutOfOffice',
            'pricePercentMultiplerNonWorkingDay': '$pricePercentMultiplerNonWorkingDay',
            'hourlyRate': '$hourlyRate'
          },
          sortField: 'name',
          propsWhen: {
            label: PROFILE_LABEL_LOGIC,
            disabled: DISABLED_WHEN_EXTERNAL_SUPPLIER_NOT_SET,
            url: (wp) => {
              return wp?.externalSupplier?.name && '/api/sales/product/supplier/' + wp.externalSupplier.name
            }
          },
          filter: this.filterProducts, // TODO: workaround in attesa di filtro su API
          required: true
        },
        externalSupplier: {
          id: 'externalSupplier',
          showWhen: SHOW_ON_EXTERNAL_WORKPACKAGE_LOGIC,
          type: 'select',
          autocomplete: true,

          url: '/api/sales/product/supplier',
          itemValue: 'id',
          itemText: 'name',
          outValue: 'externalSupplier',
          outValueExpr: {
            id: '$id',
            name: '$name'
          },
          sortField: 'name',
          required: true,
          propsWhen: {
            label: EXTERNAL_SUPPLIER_LABEL_LOGIC
          }
        },
        externalWorkpackage: {
          id: 'externalWorkpackage',
          label: 'sales.label.wpExternal',
          type: 'checkbox',
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        supplier: {
          id: 'supplier',
          label: 'sales.label.wpCompany',
          type: 'select',
          autocomplete: true,

          url: '/api/sales/product/supplier',
          itemValue: 'id',
          itemText: 'name',
          outValue: 'supplier',
          outValueExpr: {
            id: '$id',
            name: '$name'
          },
          sortField: 'name',

          required: true,
          defaultValue: this.defaultSupplier,
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        product: {
          id: 'product',
          required: true,
          type: 'select',
          autocomplete: true,
          itemValue: 'id',
          itemText: 'product',
          itemTextExpr: '$name',
          outValue: 'product',
          outValueExpr: {
            'id': '$id',
            'cost': '$cost',
            'listPrice': '$listPrice',
            'pricePercentMultiplerOutOfOffice': '$pricePercentMultiplerOutOfOffice',
            'pricePercentMultiplerNonWorkingDay': '$pricePercentMultiplerNonWorkingDay',
            'type': '$type',
            'hourlyRate': '$hourlyRate'
          },
          sortField: 'name',
          propsWhen: {
            label: PROFILE_LABEL_LOGIC,
            disabled: DISABLED_WHEN_SUPPLIER_NOT_SET,
            url: (wp) => {
              return wp?.supplier?.name && '/api/sales/product/supplier/' + wp.supplier.name
            }
          },
          filter: this.filterProducts, // TODO: workaround in attesa di filtro su API
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        jobType: {
          id: 'jobType',
          required: true,
          label: 'sales.label.wpJobType',
          type: 'select',
          options: [
            { 'text': 'sales.form.workPackage.jobType.option.workingDay', 'id': 'Lavorativo' },
            { 'text': 'sales.form.workPackage.jobType.option.nonWorkingDay', 'id': 'Festivo / Notturno' },
            { 'text': 'sales.form.workPackage.jobType.option.ooh', 'id': 'OOH' }
          ],
          header: false,
          outValue: 'jobType',
          outValueExpr: '$id',
          defaultValue: 'Lavorativo',
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        unitOfMeasure: {
          id: 'unitOfMeasure',
          required: true,
          label: 'sales.label.wpUnitOfMeasure',
          type: 'select',
          options: [
            { 'text': 'sales.form.workPackage.unitOfMeasure.option.day', 'id': 0 },
            { 'text': 'sales.form.workPackage.unitOfMeasure.option.hour', 'id': 1 }
          ],
          header: false,
          outValue: 'unitOfMeasure',
          outValueExpr: '$id',
          defaultValue: 0,
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        quantity: {
          id: 'quantity',
          type: 'number',
          required: true,
          label: this.getQuantityTitleWithType(),
          rules: { min_value: 0 }
        },
        city: {
          id: 'city',
          type: 'select',
          label: 'sales.label.city',
          autocomplete: true,
          filter: 'startsWith',
          url: '/api/sales/city',
          itemValue: 'id',
          itemText: 'city',
          itemTextExpr: '$city',
          outValue: 'city',
          outValueExpr: {
            'id': '$id'
          },
          sortField: 'city',
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        cost: {
          id: 'cost',
          type: 'number',
          label: 'sales.label.wpCost',
          disabled: true,
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        listPrice: {
          id: 'listPrice',
          type: 'number',
          label: 'sales.label.wpListPrice',
          disabled: true,
          showWhen: WP_TYPE_EQUALS_SERVICE
        },
        unitPrice: {
          id: 'unitPrice',
          type: 'number',
          label: 'sales.label.wpUnitPrice',
          rules: { min_value: 0 },
          required: true,
          propsWhen: {
            disabled: DISABLED_WHEN_SUPPLIER_NOT_SET
          },
          showWhen: WP_TYPE_EQUALS_SERVICE
        // },
        // startDate: {
        //   id: 'startDate',
        //   label: 'sales.label.wpStartDate',
        //   type: 'date',
        //   rules: {
        //     date_higher_than: [this.trackerDTO.startDate, true],
        //     date_lower_than: [this.trackerDTO.endDate, true]
        //   }
        // },
        // endDate: {
        //   id: 'endDate',
        //   label: 'sales.label.wpEndDate',
        //   type: 'date',
        //   rules: {
        //     date_lower_than: [this.trackerDTO.endDate, true],
        //     date_higher_than: ['@startDate', true] // cross validation on field "startDate"
        //   }
        },
        currency:
          this.currency
      }
    },

    formTitle () {
      const type = this.wpType
      const newLabelKey = type.formTitleNew || 'sales.label.wpNew'
      const editLabelKey = type.formTitleEdit || 'sales.label.wpEdit'
      return this.editedIndex === -1 ? this.$t(newLabelKey) : this.$t(editLabelKey)
    },

    priceGuidance () {
      return this.configuration?.workpackages?.priceGuidance
    },

    currency () {
      // In this section we merge some prop because they use js function which can't be defined in the sales.json
      let currencyConfiguration = this.configuration?.workpackages?.columns?.currency
      currencyConfiguration = Object.assign({}, currencyConfiguration,
        {
          defaultValue: this.getWorkPackageCurrency() ? this.getWorkPackageCurrency() : 'EUR',
          disabled: !!this.getWorkPackageCurrency() && (this.items.length > 1 || !this.editedItem.id),
          hint: this.currencyHint()
        })
      return currencyConfiguration
    },

    reactivityTrigger () {
      let res = ''
      if (this.trackerDTO) {
        return res
      }
      return res
    },

    totals () {
      const tots = this.items.reduce((acc, wp) => {
        // Defaulting missing values to 0
        const quantity = parseFloat(wp.quantity) || 0
        const subTotal = quantity * parseFloat(wp.unitPrice || 0)

        const subTotalCost = quantity * parseFloat(Math.max(wp.cost, wp.externalCost) || 0)

        const subTotalList = quantity * parseFloat(Math.max(wp.listPrice, wp.externalListPrice) || 0)

        acc.total = acc.total + subTotal
        acc.totalCost = acc.totalCost + subTotalCost
        acc.totalList = acc.totalList + subTotalList

        return acc
      }, {
        total: 0,
        totalCost: 0,
        totalList: 0
      })

      tots.percent = this.formatPercentage(tots.total, tots.totalList)

      // apply 2dec aprox
      tots.total = this.formatCurrency(tots.total)
      tots.totalCost = this.formatCurrency(tots.totalCost)
      tots.totalList = this.formatCurrency(tots.totalList)

      return tots
    },

    wpIsEditable () {
      const wpReadOnlyStates = this.configuration?.workpackages?.readOnlyStates || []
      return wpReadOnlyStates.indexOf(this.trackerDTO.state) === -1
    }
  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    items: {
      deep: true,
      handler (next) {
        this.$emit('workPackagesUpdated', next)
      }
    },
    trackerDTO (newValue, oldValue) {
      if (newValue?.id !== oldValue?.id) {
        // re-validate items when trackerDTO is received
        this.items = this.items.map(this.decorateWpData)
      }
    }
  },

  created () {
    this.fetchWorkPackage()
  },

  mounted: function () {
    this.$debug('TrackerDataTableItemExtend mounted')
  },

  methods: {
    ...mapActions({
      notifyError: 'notificationStore/notifyError',
      startLoader: 'loaderStore/startLoader',
      stopLoader: 'loaderStore/stopLoader'
    }),

    decorateWpData (wp) {
      const w = Object.assign({}, this.defaultItem, wp)
      if (w.externalProduct) w.externalWorkpackage = true // set flag for checkbox when external product is set

      // Match the wpType looking to the selected product
      try {
        w.wpType = this.matchWpType(wp).id
      } catch (e) {
        w.wpType = this.wpTypes[0].id // fallback to service when unset
      }

      // if (this.trackerDTO.id && !validateWackageDates(this.trackerDTO, w)) {
      //   w.invalid = true
      // }

      return w
    },

    fetchWorkPackage () {
      this.startLoader({ id: 'work-packages' })
      const url = this.agilisUrlWorkPackage(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      this.$axios.get(url, this.$axios.agilisRequestConfig(url)).then((response) => {
        this.items = response.data.map(this.decorateWpData)
      }).catch((error) => {
        this.notifyError(error)
      }).finally(() => {
        this.stopLoader({ id: 'work-packages' })
      })
    },

    formatCurrency (value) {
      if (value === null || value === undefined || value === '') return '\xA0'
      return value.toLocaleString(i18n.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    formatPercentage (current, june) {
      const percent = (current - june) / june * 100
      return percent.toLocaleString(i18n.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + '%'
    },

    createItem (wpType) {
      const startDate = this.trackerDTO.startDate || null
      const endDate = this.trackerDTO.endDate || null

      this.editedItem = Object.assign({}, this.defaultItem, {
        startDate,
        endDate,
        wpType: wpType
      })

      const defaultsFromType = this.wpType.defaultValues || {}
      this.editedItem = Object.assign(this.editedItem, defaultsFromType)

      this.editedIndex = -1
      this.dialog = true
      this.dialogReadOnly = false
    },

    copyItem (item) {
      const newItem = Object.assign(
        {},
        this.defaultItem,
        L.pickBy(item, (value, key) => key !== 'id') // remove id: on save will create a new one
      )

      this.editedItem = newItem
      this.editedIndex = -1
      this.dialog = true
      this.dialogReadOnly = false

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    editItem (item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, this.defaultItem, item)
      this.dialog = true
      this.dialogReadOnly = false

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    viewItem (item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, this.defaultItem, item)
      this.dialog = true
      this.dialogReadOnly = true

      // Edit flow: force form immediate validation
      this.forceFormValidation()
    },

    deleteItem (item) {
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },

    deleteItemConfirm (item) {
      this.startLoader({ id: 'work-packages' })
      const url = this.agilisUrlWorkPackageEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        item.id)
      this.$axios.delete(
        url,
        {
          ...this.$axios.agilisRequestConfig(url),
          data: item
        })
        .then((response) => {
          this.fetchWorkPackage()
        })
        .catch((error) => {
          this.notifyError(error)
          this.stopLoader({ id: 'work-packages' })
        })
      this.dialogDelete = false
      this.closeDelete()
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    forceFormValidation () {
      setTimeout(() => {
        this.$refs.formValidationObserver.validate()
      }, 0)
    },

    async saveWP (_item) {
      let item = Object.assign({}, _item) // work on a copy of the object to persist data in case of errors

      // Clean up wp model to prevent inconsitent values
      if (item.externalWorkpackage === false) {
        delete item.externalCost
        delete item.externalListPrice
        delete item.externalUnitPrice
        delete item.externalProduct
        delete item.externalSupplier
      } else if (item.wpType !== WP_TYPE_SERVICE) { // type !== service: we need to still map values into standard wp fields for Clann mapping
        item.product = item.externalProduct
        item.cost = item.externalCost
        item.listPrice = item.externalListPrice
        item.unitPrice = item.externalUnitPrice
      }
      delete item.externalWorkpackage
      delete item.wpType
      // ///////////////////////////////////////////////

      this.startLoader({ id: 'save-work-package' })
      if (item.id) {
        const url = this.agilisUrlWorkPackageEdit(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId,
          item.id)

        try {
          await this.$axios.put(url, item, this.$axios.agilisRequestConfig(url))
          this.fetchWorkPackage()
        } catch (error) {
          this.notifyError(error)
        }
        this.stopLoader({ id: 'save-work-package' })
        this.close()
      } else {
        item.propositionId = this.trackerId
        const url = this.agilisUrlWorkPackage(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId)
        try {
          await this.$axios.post(url, item, this.$axios.agilisRequestConfig(url))
          this.fetchWorkPackage()
        } catch (error) {
          this.notifyError(error)
        }
        this.stopLoader({ id: 'save-work-package' })
        this.close()
      }
    },

    agilisUrlWorkPackage (applicationId, trackerTypeId, trackerId) {
      return `/api/${applicationId}/tracker/${trackerTypeId}/${trackerId}/workpackage`
    },

    agilisUrlWorkPackageEdit (applicationId, trackerTypeId, trackerId, workPackageId) {
      return `/api/${applicationId}/tracker/${trackerTypeId}/${trackerId}/workpackage/${workPackageId}`
    },

    beforeChange (delta) {
      if (delta.externalUnitPrice > this.editedItem.unitPrice) { // Update unitPrice when edting externalUnitPrice with an > value
        delta.unitPrice = delta.externalUnitPrice
      }
      if (delta.product !== undefined) { // PRODUCT updated
        // Retrieve prices from selected products
        const listPrice = delta.product?.listPrice || null
        const cost = delta.product?.cost || null

        // Retrieve scale factor from JSON config
        const jobType = this.editedItem.jobType
        const scaleFactor = getPriceScaleFactor(delta.product, jobType)

        // Update the model
        if (this.editedItem.currency) {
          delta.cost = this.convertFromEUR(cost, this.editedItem.currency)
          delta.listPrice = (this.convertFromEUR(listPrice, this.editedItem.currency) * scaleFactor).toFixed(2)
          delta.unitPrice = delta.listPrice
        } else {
          delta.cost = cost
          delta.listPrice = (listPrice * scaleFactor).toFixed(2)
          delta.unitPrice = delta.listPrice
        }

        // Update unit of measure according to product
        const hourlyRate = delta.product.hourlyRate
        delta.unitOfMeasure = hourlyRate ? 1 : 0

        if (parseFloat(this.editedItem.externalUnitPrice) > parseFloat(delta.unitPrice)) {
          // Update the unitPrice when the external product is selected and its unitPrice is >
          delta.cost = this.editedItem.externalCost
          delta.listPrice = this.editedItem.externalListPrice
          delta.unitPrice = this.editedItem.externalUnitPrice
        }
      }
      if (delta.jobType !== undefined && this.editedItem.listPrice) { // JOB TYPE updated
        // Retrieve scale factor from JSON config
        const jobType = delta.jobType
        const scaleFactor = getPriceScaleFactor(this.editedItem.product, jobType)
        delta.listPrice = (this.convertFromEUR(this.editedItem.product.listPrice, this.editedItem.currency) * scaleFactor).toFixed(2)
        delta.unitPrice = delta.listPrice
      }
      if (delta.externalProduct !== undefined) { // External product updated: update exteral[cost, listPrice, UnitPrice]
        const externalCost = delta.externalProduct.cost
        const externalListPrice = delta.externalProduct.listPrice

        if (this.editedItem.currency) {
          delta.externalCost = this.convertFromEUR(externalCost, this.editedItem.currency)
          delta.externalListPrice = this.convertFromEUR(externalListPrice, this.editedItem.currency)
          delta.externalUnitPrice = delta.externalListPrice
        } else {
          delta.externalCost = externalCost
          delta.externalListPrice = externalListPrice
          delta.externalUnitPrice = externalListPrice
        }

        if (parseFloat(this.editedItem.unitPrice) < parseFloat(delta.externalUnitPrice)) { // Update unitPrice when externalUnitPrice is >
          delta.cost = delta.externalCost
          delta.listPrice = delta.externalListPrice
          delta.unitPrice = delta.externalUnitPrice
        }
      }
      if (delta.externalSupplier !== undefined) {
        // Change of supplier: reset costs & product
        delta.externalCost = null
        delta.externalListPrice = null
        delta.externalUnitPrice = null
        delta.externalProduct = null
      }
      if (delta.currency !== undefined && (this.editedItem.listPrice || this.editedItem.externalProduct)) { // Currency updated
        if (this.editedItem.listPrice) {
          const jobType = this.editedItem.jobType
          const scaleFactor = getPriceScaleFactor(this.editedItem.product, jobType)

          delta.cost = this.convertFromEUR(this.editedItem.product.cost, delta.currency)
          delta.listPrice = (this.convertFromEUR(this.editedItem.product.listPrice, delta.currency) * scaleFactor).toFixed(2)
          delta.unitPrice = delta.listPrice
        }
        if (this.editedItem.externalProduct) {
          delta.externalCost = this.convertFromEUR(this.editedItem.externalProduct.cost, delta.currency)
          delta.externalListPrice = this.convertFromEUR(this.editedItem.externalProduct.listPrice, delta.currency)
          delta.externalUnitPrice = delta.externalListPrice
        }
      }

      return delta
    },

    getQuantityTitleWithType () {
      let quantityLabel = 'sales.label.wpQuantityDays'
      for (const option of this.configuration.columns.type.options) {
        if (option.id === this.trackerDTO.type && option.wpQuantityLabel) {
          quantityLabel = option.wpQuantityLabel
          break
        }
      }
      return quantityLabel
    },

    userCanDoHttp (operation) {
      const tmp = Configuration.auth.userCanAccessFeature(this.selectedContext, 'workpackage', operation, this.trackerDTO)
      return tmp
    },

    userCanDoAction (buttonId) {
      const feature = `workpackage/datatable/${buttonId}`
      const tmp = Configuration.auth.userCanAccessFeature(this.selectedContext, feature, null, this.trackerDTO)
      return tmp
    },

    userCanDataTableAction (operation) {
      let tmp
      const wpEditable = this.wpIsEditable

      switch (operation) {
        case 'view':
          tmp = this.userCanDoAction('view') ||
            (!wpEditable && this.userCanDataTableAction('edit')) // fallback to view when user canEdit but wp is not editable
          break

        case 'create':
          tmp = this.userCanDoHttp('post') && this.userCanDoAction('create') && wpEditable
          break

        case 'copy':
          tmp = this.userCanDoHttp('post') && this.userCanDoAction('copy') && wpEditable
          break

        case 'delete':
          tmp = this.userCanDoHttp('delete') && this.userCanDoAction('delete') && wpEditable
          break

        case 'edit':
          tmp = this.userCanDoHttp('put') && this.userCanDoAction('edit') && wpEditable
          break

        case 'editInline':
          tmp = this.userCanDoHttp('put') && this.userCanDoAction('editInline') && wpEditable

          break

        default:
          break
      }

      return tmp
    },

    fieldTitle (required, label) {
      return Configuration.datatable.getRequiredLabel({ required, label })
    },

    renderJobType (jobType) {
      const opt = this.formFields.jobType.options.find(j => j.id === jobType)
      if (opt) return this.$t(opt.text)
    },

    priceMarginColor (item) {
      const priceGuidanceLogic = this.priceGuidance.jsonLogic
      const defaultColor = 'transparent'

      if (priceGuidanceLogic) {
        try {
          const jlContext = {
            cost: Math.max(item.cost, item.externalCost),
            listPrice: Math.max(item.listPrice, item.externalListPrice),
            unitPrice: item.unitPrice,

            internalCost: item.cost,
            internalListPrice: item.listPrice,

            externalWorkpackage: !!item.externalWorkpackage,
            externalCost: item.externalCost,
            externalListPrice: item.externalListPrice,
            externalUnitPrice: item.externalUnitPrice,

            quantity: item.quantity,
            totalPrice: item.quantity ? (item.quantity * item.unitPrice) : item.unitPrice
          }
          const logicOutput = jsonLogic.apply(priceGuidanceLogic, jlContext)
          return logicOutput || defaultColor
        } catch (e) {
          console.debug('Invalid jsonLogic', e)
        }
        return defaultColor
      }
    },

    userCanAccessField (colId) {
      return colId === 'actions' ||
        Configuration.auth.userCanAccessField(this.selectedContext, colId, 'workpackage')
    },

    userCanEditField (colId) {
      return Configuration.auth.userCanAccessField(this.selectedContext, colId, 'workpackage', 'edit')
    },

    async inlinePriceEdit (item, newPrice) {
      if (parseFloat(item.unitPrice) !== parseFloat(newPrice)) {
        const delta = { unitPrice: newPrice }

        // When unitPrice is not visible because just externalUnitPrice is exposed, update also field externalUnitPrice
        if (item.wpType !== WP_TYPE_SERVICE) { delta.externalUnitPrice = newPrice }

        const wpUpdated = Object.assign({}, item, delta)
        await this.saveWP(wpUpdated)
      }
    },

    filterProducts (p) {
      return this.wpType && this.wpType.productTypes.indexOf(p.type) !== -1
    },

    matchWpType (wp) {
      return this.wpTypes.find(t => t.productTypes.indexOf(wp.product.type) !== -1)
    },

    wpTypeIcon (wp) {
      const wpType = this.matchWpType(wp)
      return wpType && wpType.icon
    },

    wpTypeName (wp) {
      const wpType = this.matchWpType(wp)
      return wpType && wpType.label
    },

    showCurrencyTooltip () {
      return this.items.some(item => {
        if (item.currency) {
          return item.currency !== this.getWorkPackageCurrency()
        }
      })
    },

    getWorkPackageCurrency () {
      return this.items.find(item => item.currency)?.currency
    },

    currencyHint () {
      if (this.editedItem.currency !== 'EUR') {
        let exchangeRateEUR
        const option = this.configuration?.workpackages?.columns?.currency?.options.find(o => o['id'] === this.editedItem.currency)
        if (option) {
          exchangeRateEUR = option['exchangeRateEUR']
        }
        return this.editedItem.currency + ' = ' + 'EUR x ' + exchangeRateEUR
      }
    },

    convertFromEUR (value, wpCurrency) {
      let colConfig = this.formFields.currency
      let exchangeRateEUR = 1
      if (colConfig.options) { // options defined in config
        const option = colConfig.options.find(o => o['id'] === wpCurrency)
        if (option) {
          exchangeRateEUR = option['exchangeRateEUR']
        }
      }
      return (value * exchangeRateEUR).toFixed(2)
    }
  }
}
